import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info, X } from "lucide-react";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { trendNote } from "@/components/dashboard/ScoreTrend";

// Plain-language explanations for each dashboard gauge. Facts are filled from the live payload — nothing invented.
const n = (v) => (v == null ? "—" : Number(v).toLocaleString());

export const gaugeExplainers = (d) => {
  const m = d.current.market, s = d.current.strategy, dir = m.directional || {}, nd = m.non_directional || {};
  const needM = m.needed || {}, needS = s.needed || {};
  return {
    main: {
      title: "EV-IDEX Prediction Accuracy Score",
      what: "A 0–100 score built only from EV-IDEX's own market predictions after they were sealed, then graded against the real price. Nothing is edited after grading.",
      how: `It blends directional accuracy, non-directional accuracy, how well strong calls beat weak ones, calibration, and consistency across market regimes and timeframes. ${m.sample_factor != null && m.sample_factor < 1 ? `A sample factor of ${m.sample_factor} currently shrinks the raw score because the sample is still small.` : ""}`,
      status: m.status === "insufficient"
        ? `"Score not published yet" means the minimum sample is not met yet: ${n(m.resolved)} independent graded predictions (needs ${n(m.resolved + (needM.resolved || 0))}) and ${n(dir.n)} directional calls (needs ${n((dir.n || 0) + (needM.directional || 0))}). No score is published until then — the gauge is honestly empty rather than showing a number that could swing wildly.`
        : m.status === "preliminary" ? `"Preliminary" means the minimum sample is met but the sample is still small, so the score can move a lot as more predictions are graded.` : `"Established" means the sample is large enough for the score to be reasonably stable.`,
      trend: trendNote(d),
      caveat: "Accuracy is a hit rate, not profit. Past results never guarantee future ones.",
    },
    directional: {
      title: "Directional accuracy",
      what: "Of the Bullish or Bearish calls that were graded right or wrong, the share that were right.",
      how: `${n(dir.correct)} correct of ${n(dir.n)} eligible calls = ${dir.accuracy == null ? "—" : `${dir.accuracy}%`}. The 95% confidence interval (${dir.wilson_ci_95 ? `${dir.wilson_ci_95[0]}–${dir.wilson_ci_95[1]}%` : "—"}) shows how uncertain that figure still is: a wide range means few calls so far.`,
      status: `${n(dir.neutral)} outcomes that ended inside the neutral band and ${n(dir.excludes?.non_directional_calls)} WAIT / Neutral calls are excluded — they can't be "right" or "wrong" about direction, so counting them would inflate the number.`,
      caveat: "50% is the coin-flip baseline; a figure above it only matters once the confidence interval also sits above it.",
    },
    nondirectional: {
      title: "Non-directional accuracy",
      what: "How often WAIT / Neutral calls were correct — i.e. the market really did stay inside the expected band instead of trending.",
      how: `${n(nd.correct)} correct of ${n(nd.n)} calls = ${nd.accuracy == null ? "—" : `${nd.accuracy}%`}${nd.wilson_ci_95 ? ` (95% CI ${nd.wilson_ci_95[0]}–${nd.wilson_ci_95[1]}%)` : ""}. Each WAIT / Neutral call counts once, as right or wrong, after its window closes.`,
      status: "This is reported separately from directional accuracy so that cautious calls never pad the headline hit rate.",
      caveat: "A high value here means EV-IDEX was right to stay out — it is not a measure of gains.",
    },
    strategy: {
      title: "Strategy Outlook score",
      what: "The same 0–100 scoring applied to EV-IDEX's BUY / SELL / AVOID strategy calls (the Opportunities outlook), graded separately from market predictions.",
      how: `${n(s.resolved)} independent graded strategy calls so far${s.directional ? `, ${n(s.directional.n)} of them directional` : ""}.`,
      status: s.status === "insufficient"
        ? `"Insufficient data" means it needs ${n(s.resolved + (needS.resolved || 0))} graded calls and ${n((s.directional?.n || 0) + (needS.directional || 0))} directional ones before a score is published — far fewer strategy calls have closed than market predictions, so this gauge fills later.`
        : `Status: ${s.status}.`,
      caveat: "Strategy calls and market predictions are never mixed into one number.",
    },
  };
};

// Visual marker only — the surrounding gauge card is the single interactive trigger (no nested buttons).
export const GaugeInfoIcon = ({ tid, className = "" }) => (
  <span aria-hidden="true" data-testid={tid} className={`inline-flex shrink-0 items-center justify-center w-6 h-6 min-w-[24px] rounded-full border border-slate-600/80 text-slate-400 transition-colors group-hover:text-cyan-200 group-hover:border-cyan-600 group-focus-visible:text-cyan-200 group-focus-visible:border-cyan-600 ${className}`}>
    <Info className="w-3.5 h-3.5" />
  </span>
);

const hoverCapable = () => typeof window !== "undefined" && window.matchMedia?.("(hover: hover)").matches;
const isDesktop = () => typeof window !== "undefined" && window.matchMedia?.("(min-width: 1024px)").matches;

const InfoBody = ({ item, tid }) => (
  <div className="space-y-2.5 text-[13px] text-slate-300 leading-relaxed" data-testid={`${tid}-popover-body`}>
    <p><strong className="text-slate-100">What it measures.</strong> {item.what}</p>
    <p><strong className="text-slate-100">How it's calculated.</strong> {item.how}</p>
    <p><strong className="text-slate-100">What the status means.</strong> {item.status}</p>
    {item.trend && <p data-testid={`${tid}-popover-trend`}><strong className="text-slate-100">30-day trend.</strong> {item.trend}</p>}
    <p className="text-xs text-slate-500">{item.caveat}</p>
  </div>
);

// Phone / tablet: compact non-modal bottom sheet above the mobile nav. The page behind stays scrollable and visible.
const MobileSheet = ({ item, tid, cardRef, onClose }) => {
  const sheet = useRef(null);
  useEffect(() => {
    const down = (e) => { if (!sheet.current?.contains(e.target) && !cardRef.current?.contains(e.target)) onClose(); };
    const key = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("pointerdown", down); document.addEventListener("keydown", key);
    return () => { document.removeEventListener("pointerdown", down); document.removeEventListener("keydown", key); };
  }, [cardRef, onClose]);
  return createPortal(
    <div ref={sheet} role="dialog" aria-modal="false" aria-labelledby={`${tid}-popover-title`} data-testid={`${tid}-popover`}
      className="fixed left-3 right-3 z-50 flex flex-col rounded-2xl border border-cyan-700/70 text-slate-100 shadow-[0_-12px_40px_rgba(0,0,0,0.7)] max-h-[42vh] animate-in slide-in-from-bottom-4 fade-in-0 duration-200"
      style={{ bottom: "calc(60px + env(safe-area-inset-bottom))", background: "rgba(13,18,27,0.98)" }}>
      <div className="flex items-start gap-3 px-4 pt-3 pb-2 border-b border-slate-800/80 shrink-0">
        <div className="min-w-0 flex-1">
          <div id={`${tid}-popover-title`} className="font-display text-sm tracking-normal text-slate-50" data-testid={`${tid}-popover-title`}>{item.title}</div>
          <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500 mt-0.5">Figures come from the sealed prediction ledger</div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close explanation" data-testid={`${tid}-popover-close`}
          className="inline-flex items-center justify-center w-9 h-9 min-w-[36px] rounded-full border border-slate-700 text-slate-300 hover:text-cyan-200 hover:border-cyan-600 active:scale-95 transition">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="overflow-y-auto overscroll-contain px-4 py-3"><InfoBody item={item} tid={tid} /></div>
    </div>,
    document.body
  );
};

// Desktop: hover (pointer devices) + keyboard focus + click, anchored to the whole gauge card; content is placed outside the
// card and flips/shifts to stay on screen. Below 1024px the same trigger opens the MobileSheet instead.
export const GaugePopover = ({ item, tid, className = "", style, side = "bottom", children }) => {
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [placement, setPlacement] = useState("bottom");
  const card = useRef(null), timer = useRef(null), pinned = useRef(false);
  const show = () => { clearTimeout(timer.current); setMobile(!isDesktop()); setPlacement(window.matchMedia("(min-width: 1280px)").matches ? side : "bottom"); setOpen(true); };
  const close = () => { pinned.current = false; clearTimeout(timer.current); setOpen(false); };
  const hide = () => { if (pinned.current) return; clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(false), 140); };
  const toggle = () => {
    if (pinned.current) { close(); return; }
    pinned.current = true;
    // Small screens: park the tapped card just under the sticky header so it stays visible above the bottom sheet.
    if (!isDesktop() && card.current) {
      const top = card.current.getBoundingClientRect().top;
      if (top < 124 || top > 200) window.scrollBy({ top: top - 124, behavior: "auto" });
    }
    show();
  };
  const onOpenChange = (o) => { if (!o) close(); else setOpen(true); };
  const trigger = (
    <div ref={card} role="button" tabIndex={0} aria-haspopup="dialog" aria-expanded={open} aria-label={`${item.title} — explanation`}
      data-testid={tid} data-state={open ? "open" : "closed"} style={style}
      className={`group cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/70 ${className}`}
      onMouseEnter={() => hoverCapable() && isDesktop() && show()} onMouseLeave={() => hoverCapable() && isDesktop() && hide()}
      onFocus={() => { if (isDesktop()) show(); }} onBlur={(e) => { if (!card.current?.contains(e.relatedTarget)) hide(); }}
      onClick={toggle}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } else if (e.key === "Escape") close(); }}>
      {children}
    </div>
  );
  if (mobile) return <>{trigger}{open && <MobileSheet item={item} tid={tid} cardRef={card} onClose={close} />}</>;
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverAnchor asChild>{trigger}</PopoverAnchor>
      <PopoverContent side={placement} align={placement === "bottom" ? "center" : "start"} sideOffset={10} collisionPadding={{ top: 124, left: 12, right: 12, bottom: 16 }}
        onOpenAutoFocus={(e) => e.preventDefault()} onInteractOutside={(e) => { if (card.current?.contains(e.target)) e.preventDefault(); }}
        onMouseEnter={() => hoverCapable() && show()} onMouseLeave={() => hoverCapable() && hide()}
        style={{ maxHeight: "min(70vh, var(--radix-popover-content-available-height))" }}
        className="w-[min(92vw,24rem)] overflow-y-auto rounded-xl border-slate-700/80 bg-[#0F141C]/95 backdrop-blur-md text-slate-100 shadow-[0_18px_50px_rgba(0,0,0,0.55)] p-4"
        data-testid={`${tid}-popover`}>
        <div className="font-display text-sm tracking-normal text-slate-50" data-testid={`${tid}-popover-title`}>{item.title}</div>
        <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500 mt-0.5 mb-3">Figures come from the sealed prediction ledger</div>
        <InfoBody item={item} tid={tid} />
      </PopoverContent>
    </Popover>
  );
};
